import React from "react"
import { Box, Grid, FormControlLabel, Checkbox, Button as MUIButton } from "@mui/material"
import { makeStyles } from "makeStyles"
import Skeleton from "@mui/material/Skeleton"

import Price from "components/Price"
import LazyLoadedImage from "components/LazyLoadedImage"
import ChangeAmountButton from "components/Buttons/ChangeAmountButton"

import { netToGross } from "@secureo/common/utils/tax"

import { ProductConfigurationsBySku } from "@secureo/common/reducers/productDetailPageReducer"
import {
	ConfigurationGroup as ConfigurationGroupType,
	ConfigurationGroupProduct,
	ConfigurationGroupProductsBySku,
} from "@secureo/common/typings/Product"
import { CountryCode } from "@secureo/common/typings/CountryCode"

const useStyles = makeStyles()({
	gridSingle: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-start",
		width: "100%",
		"@media only screen and (max-width: 600px)": {
			display: "block",
		},
	},
	gridAmount: {
		display: "flex",
		alignItems: "center",
		width: "100%",
		"@media only screen and (max-width: 600px)": {
			display: "block",
			margin: "auto",
			textAlign: "center",
		},
	},
	changeAmountPosition: {
		display: "block",
		"@media only screen and (max-width: 600px)": {
			display: "flex",
			justifyContent: "center",
		},
	},
	image: {
		height: 70,
		width: 70,
		objectFit: "contain",
		marginLeft: 7,
		mixBlendMode: "multiply",
	},
})

const ConfigurationGroupMultiConfiguration = ({
	isLoading,
	configurationProduct,
	grossPriceCents,
	quantity,
	maxQuantity,
	setConfigurationQuantity,
}: ConfigurationGroupMultiConfigurationProps) => {
	const { classes } = useStyles()

	const { sku, name, image, description } = configurationProduct

	const toggleConfiguration = (sku: string, event: React.ChangeEvent<HTMLInputElement>) => {
		const isChecked = event.target.checked

		setConfigurationQuantity(sku, isChecked ? 1 : 0)
	}

	return (
		<Grid item xs={12}>
			{maxQuantity === 1 ? (
				<MUIButton
					style={{
						border: quantity > 0 ? "1px solid #4591CE" : "1px solid whitesmoke",
						background: quantity > 0 ? "#E7EFF6" : "white",
						borderRadius: 10,
						textTransform: "inherit",
						textAlign: "left",
						fontFamily: "Muli",
						boxShadow: " 0px 0px 5px 0px rgba(0,0,0,0.15)",
						width: "100%",
					}}
					onChange={(event) => {
						toggleConfiguration(sku, event as any)
					}}
				>
					<FormControlLabel
						checked={quantity > 0}
						control={<Checkbox color="primary" />}
						style={{ marginRight: "auto" }}
						label={
							<Box p={1} className={classes.gridSingle}>
								<>
									{image ? (
										<LazyLoadedImage
											src={image}
											title={name}
											alt={name}
											className={classes.image}
										/>
									) : (
										<Skeleton
											variant="rectangular"
											width={73}
											height={70}
											animation={false}
										/>
									)}
								</>
								<Box pr={1} mt={1} ml={1}>
									<strong>
										{name} + <Price priceInEuroCents={grossPriceCents} />
									</strong>

									{description && (
										<Box pr={1}>
											<p
												dangerouslySetInnerHTML={{
													__html: description,
												}}
											/>
										</Box>
									)}
								</Box>
							</Box>
						}
						labelPlacement="end"
						disabled={isLoading}
					/>
				</MUIButton>
			) : (
				<Box
					p={1}
					style={{
						border: quantity > 0 ? "1px solid #4591CE" : "1px solid whitesmoke",
						background: quantity > 0 ? "#E7EFF6" : "white",
						borderRadius: 10,
						textTransform: "inherit",
						textAlign: "left",
						boxShadow: " 0px 0px 5px 0px rgba(0,0,0,0.15)",
					}}
				>
					<Box p={1} className={classes.gridAmount}>
						<Box pr={2} style={{ textAlign: "center" }}>
							{image ? (
								<LazyLoadedImage
									src={image}
									title={name}
									alt={name}
									className={classes.image}
								/>
							) : (
								<Skeleton
									variant="rectangular"
									width={73}
									height={70}
									animation={false}
								/>
							)}
							<Box mt={1} className={classes.changeAmountPosition}>
								<ChangeAmountButton
									quantity={quantity}
									onChange={(quantity: number) =>
										setConfigurationQuantity(sku, quantity)
									}
									minQuantity={0}
									maxQuantity={maxQuantity}
									disabled={isLoading}
								/>
							</Box>
						</Box>

						<Box pr={1} mt={1} ml={1}>
							<strong>
								{name} + <Price priceInEuroCents={grossPriceCents} />
							</strong>
							{description && (
								<Box pr={1}>
									<p dangerouslySetInnerHTML={{ __html: description }} />
								</Box>
							)}
						</Box>
					</Box>
				</Box>
			)}
		</Grid>
	)
}

interface ConfigurationGroupMultiConfigurationProps {
	isLoading: boolean
	configurationProduct: ConfigurationGroupProduct
	quantity: number
	grossPriceCents: number
	maxQuantity: number
	setConfigurationQuantity: (sku: string, quantity: number) => void
}

const ConfigurationGroupMulti = ({
	isLoading,
	configurationGroup,
	configurationGroupProductsBySku,
	selectedConfigurationsBySku,
	taxRate,
	countryCode,
	setMultiConfigurationGroupConfiguration,
}: Props) => {
	const { name: configurationGroupName, items } = configurationGroup

	const setConfigurationQuantity = (sku: string, quantity: number) => {
		setMultiConfigurationGroupConfiguration(
			configurationGroupProductsBySku,
			configurationGroupName,
			sku,
			quantity,
		)
	}

	return (
		<Grid container spacing={3}>
			{items.map((configurationGroupItem) => {
				const { sku, maxQuantity } = configurationGroupItem
				const configurationProduct = configurationGroupProductsBySku?.[sku]

				const netPriceCents = configurationProduct?.pricesByCountryCode?.[countryCode] ?? 0
				const grossPriceCents = Math.round(netToGross(netPriceCents, taxRate))

				if (!configurationProduct) return null

				const quantity = selectedConfigurationsBySku?.[sku]?.quantity ?? 0

				return (
					<ConfigurationGroupMultiConfiguration
						isLoading={isLoading}
						key={sku}
						configurationProduct={configurationProduct}
						grossPriceCents={grossPriceCents}
						quantity={quantity}
						maxQuantity={maxQuantity}
						setConfigurationQuantity={setConfigurationQuantity}
					/>
				)
			})}
		</Grid>
	)
}

interface Props {
	isLoading: boolean
	configurationGroup: ConfigurationGroupType
	configurationGroupProductsBySku: ConfigurationGroupProductsBySku
	selectedConfigurationsBySku: ProductConfigurationsBySku
	setMultiConfigurationGroupConfiguration: (
		configurationGroupProductsBySku: ConfigurationGroupProductsBySku,
		configurationGroupName: string,
		sku: string,
		quantity: number,
	) => void
	taxRate: number
	countryCode: CountryCode
}

export default ConfigurationGroupMulti
